"use strict"

import React from "react"
import ReactDOM from "react-dom"

import FeatureTriangles from "./FeatureTriangles"
import ActionDetailOverlay from "./ActionDetailOverlay"
import { withApplicationConfig } from "../../../../../components/shared_components_registration"
import { withPlotSettingsConfig } from "../../../../config/PlotSettingsConfigProvider"
import { sortDates } from "../../../../utils"
import { withGraphState } from "../../../../config/GraphConfigProvider"
import { withAnnotationsConfig } from "../../../../config/AnnotationConfigProvider"

/*
 * Action/annotation triangles and detailed overlay on mouseover - Meant to align with a temporal X-Axis.
 * Should not show on downloaded images or on graphs that do not have temporal X-Axis (e.g. scatter plots (ObsVsObs))
 * */

export const fillColors = {
  site_action: "var(--lanzalake-orange)",
  run_action: "var(--lanzalake-green-primary)",
  run_action_critical: "var(--lanzalake-red)",
  not_tag_specific_ano: "var(--lanzalake-yellow)",
  tag_specific_ano: "var(--lanzalake-blue-light)",
  ino_date: "var(--lanzalake-purple)",
}

const getInoMarker = (runInoculationDate) =>
  runInoculationDate
    ? {
        day: 0,
        timestamp: runInoculationDate,
        priority: "NORM",
        comment: "Run Inoculated",
        action_type: "ino_date",
      }
    : null

export const getCommentsList = (
  runInoculationDate,
  show_site_actions,
  site_actions = [],
  show_actions,
  actions = [],
  show_critical_actions,
  show_annotations,
  annotations = [],
) => {
  const inoMarker = getInoMarker(runInoculationDate)

  let actionItems = []
  if (show_site_actions) {
    actionItems = [
      ...actionItems,
      ...site_actions.map((a) => {
        return { ...a, action_type: "site_action" }
      }),
    ]
  }
  if (show_actions) {
    actionItems = [
      ...actionItems,
      ...actions
        .filter((action) => action?.priority !== "CRIT")
        .map((a) => {
          return { ...a, action_type: "run_action" }
        }),
    ]
  }
  if (show_critical_actions) {
    actionItems = [
      ...actionItems,
      ...actions
        .filter((action) => action?.priority === "CRIT")
        .map((a) => {
          return { ...a, action_type: "run_action_critical" }
        }),
    ]
  }
  if (show_annotations) {
    actionItems = [
      ...actionItems,
      ...annotations.map((a) => {
        return a.tags.length === 0
          ? { ...a, action_type: "not_tag_specific_ano" }
          : { ...a, action_type: "tag_specific_ano" }
      }),
    ]
  }
  return inoMarker ? [...actionItems, inoMarker] : actionItems
}

class ActionLayer extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      rootNode: document.getElementById("application-page"),
    }

    this.overlayMouseMove = this.overlayMouseMove.bind(this)
    this.overlayMouseOut = this.overlayMouseOut.bind(this)
    this.overlayMouseClick = this.overlayMouseClick.bind(this)

    // Because this component exists in SVG-land, we'll be using
    // a React portal to inject the mouseover detail popup higher
    // up the DOM.
    this.mouseoverHolder = document.createElement("div")
    this.state.rootNode.appendChild(this.mouseoverHolder)
  }

  componentWillUnmount() {
    if (this.state.rootNode.contains(this.mouseoverHolder)) {
      this.state.rootNode.removeChild(this.mouseoverHolder)
    }
  }

  render() {
    const {
      x,
      timeExtractor, // From PlotSettingsConfigProvider
      selected_timezone, // From ApplicationConfigProvider
      runInoculationDate,
      actions = [],
      site_actions = [],
      annotations = [],
      margin,
      height,
      clipID,
      download = false, // A value of true indicates that this graph is meant for PNG download

      show_site_actions = true, // From withGraphState
      show_actions = true, // From withGraphState
      show_critical_actions = true, // From withGraphState
      show_annotations = true, // From withGraphState
    } = this.props

    let overlayEvents = []
    if (this.state.overlayEventRange) {
      const [min, max] = this.state.overlayEventRange

      const actionItems = getCommentsList(
        runInoculationDate,
        show_site_actions,
        site_actions,
        show_actions,
        actions,
        show_critical_actions,
        show_annotations,
        annotations,
      )

      overlayEvents = sortDates(
        actionItems.filter((act) => {
          const time = timeExtractor.getActionTime(act, selected_timezone)
          return time >= min && time <= max
        }),
      )
    }

    const triangle_variables = {
      selected_timezone: selected_timezone,
      timeExtractor: timeExtractor,
      x: x,
    }

    const inoMarker = getInoMarker(runInoculationDate)

    return (
      <>
        {!download && (inoMarker || actions || site_actions || annotations) && (
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            <g transform={`translate(0, ${height})`} clipPath={`url(#${clipID})`}>
              {inoMarker && (
                <FeatureTriangles
                  feature_list={[inoMarker]}
                  getFillColor={(_) => fillColors["ino_date"]}
                  {...triangle_variables}
                />
              )}

              {show_site_actions && (
                <FeatureTriangles
                  feature_list={site_actions || []}
                  getFillColor={(_) => fillColors["site_action"]}
                  {...triangle_variables}
                />
              )}
              {show_actions && (
                <FeatureTriangles
                  feature_list={(actions || []).filter((action) => action?.priority !== "CRIT")}
                  getFillColor={(_) => fillColors["run_action"]}
                  {...triangle_variables}
                />
              )}
              {show_critical_actions && (
                <FeatureTriangles
                  feature_list={(actions || []).filter((action) => action?.priority === "CRIT")}
                  getFillColor={(_) => fillColors["run_action_critical"]}
                  {...triangle_variables}
                />
              )}
              {show_annotations && (
                <FeatureTriangles
                  feature_list={annotations || []}
                  getFillColor={(ano) =>
                    ano.tags.length === 0
                      ? fillColors["not_tag_specific_ano"]
                      : fillColors["tag_specific_ano"]
                  }
                  {...triangle_variables}
                />
              )}
              <rect
                x={0}
                y={0}
                width={x.range()[1]}
                height={10}
                fill="white"
                opacity={0}
                onMouseMove={this.overlayMouseMove}
                onMouseOut={this.overlayMouseOut}
                onClick={() => this.overlayMouseClick(overlayEvents)}
                ref={(rect) => {
                  this.hoverCatcher = rect
                }}
              />
              {this.state.overlayX &&
                overlayEvents &&
                overlayEvents.length > 0 &&
                ReactDOM.createPortal(
                  <div
                    className="solid-black-border"
                    style={{
                      position: "absolute",
                      left: `${this.state.overlayX - 100}px`,
                      top: `${this.state.overlayY + 10}px`,
                      zIndex: "1800",
                      background: "var(--lanzalake-white)",
                      padding: "5px",
                    }}
                  >
                    <ActionDetailOverlay
                      events={overlayEvents}
                      timeExtractor={timeExtractor}
                      selected_timezone={selected_timezone}
                    />
                  </div>,
                  this.mouseoverHolder,
                )}
            </g>
          </g>
        )}
      </>
    )
  }

  overlayMouseMove(ev) {
    const catcherBox = this.hoverCatcher.getBoundingClientRect(),
      refPoint = ev.clientX - catcherBox.x

    this.setState({
      overlayX: ev.pageX,
      overlayY: ev.pageY,
      overlayEventRange: [this.props.x.invert(refPoint - 5), this.props.x.invert(refPoint + 5)],
    })
  }

  overlayMouseOut(ev) {
    this.setState({
      overlayEventRange: null,
      overlayX: null,
    })
  }

  overlayMouseClick(events) {
    const toggleable_action_types = ["not_tag_specific_ano", "tag_specific_ano"]

    const toggleable_events = events.filter((ev) => toggleable_action_types.includes(ev.action_type))
    if (toggleable_events.length === 0) return

    const events_to_update = []
    toggleable_events.forEach((ev) => {
      const ano_id = ev.id
      const isTextVisible = this.props.annotation_visibility_state[ano_id]["show_text"]
      const isLineVisible = this.props.annotation_visibility_state[ano_id]["show_line"]

      if (this.props.show_annotation_lines === false) {
        this.props.toggleAnnotationsView()
      }

      // show text if only line is visible, hide text if line is not visible
      if ((!isTextVisible && isLineVisible) || (isTextVisible && !isLineVisible)) {
        events_to_update.push({ id: ano_id, metric: "show_text" })
      }
      // show line if neither are visible
      else if (!isTextVisible && !isLineVisible) {
        events_to_update.push({ id: ano_id, metric: "show_line" })
      }
      // remove both if they are both visible
      else {
        events_to_update.push({ id: ano_id, metric: "show_text" })
        events_to_update.push({ id: ano_id, metric: "show_line" })
      }
    })

    this.props.toggleMultipleAnnotationsFeature(events_to_update)
  }
}

export default withApplicationConfig(
  withGraphState(withPlotSettingsConfig(withAnnotationsConfig(ActionLayer))),
)
